/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ScoreCardTemplatesService {

    /**
     * List scorecard templates (server-side search, sort + pagination)
     * @param query name search (ILIKE)
     * @param page
     * @param perPage
     * @param sort column to sort by; defaults to name. An unrecognised value falls back to the default rather than erroring.
     * @param direction sort direction; anything other than "desc" is ascending
     * @returns any returns the account scorecards
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorScoreCardTemplates(
        query?: string,
        page?: number,
        perPage?: number,
        sort?: 'name' | 'updated_at',
        direction?: 'asc' | 'desc',
    ): CancelablePromise<{
        score_card_templates?: Array<{
            id?: number;
            name?: string;
            template_type?: 'unweighted' | 'weighted' | 'text';
            questions_count?: number;
            /**
             * stages holding this scorecard, for the "Attached to" panel: attached stages and rule stages that read its result
             */
            attached_stages?: Array<{
                id?: number;
                title?: string | null;
                stage_slug?: string | null;
                /**
                 * opening slug for the workflow-editor link; null when a workflow serves no opening
                 */
                funnel_slug?: string | null;
                /**
                 * the stage container's title: the workflow, or the opening for a funnel-owned stage
                 */
                workflow?: string | null;
            }>;
            /**
             * distinct stages holding this scorecard, attached and rule stages together; blocks deletion when > 0
             */
            stage_usage_count?: number;
            updated_at?: string;
            /**
             * display name of whoever last created or edited the template; null on untouched rows
             */
            updated_by?: string | null;
        }>;
        pagination?: {
            current_page?: number;
            next_page?: number | null;
            per_page?: number | null;
            total_count?: number;
            total_pages?: number;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/score_card_templates',
            query: {
                'query': query,
                'page': page,
                'per_page': perPage,
                'sort': sort,
                'direction': direction,
            },
        });
    }

    /**
     * Create a scorecard and its entries
     * @param requestBody
     * @returns any created
     * @throws ApiError
     */
    public static postInternalApiWorkflowEditorScoreCardTemplates(
        requestBody: {
            score_card_template: {
                name: string;
                template_type?: 'unweighted' | 'weighted' | 'text';
                /**
                 * The full question list, in display order. Any existing question left out is
                 * deleted, along with the answers already recorded against it — so omit the
                 * `questions` key entirely to leave the existing list untouched, and send an
                 * empty array only to clear it. The overall recommendation is added and moved
                 * last server-side, so the payload never has to carry it; when it is left out
                 * its `required` setting is preserved, defaulting to true on a new scorecard.
                 *
                 */
                questions?: Array<{
                    /**
                     * omit to create; ids not owned by this template are ignored
                     */
                    id?: number | null;
                    text: string;
                    description?: string | null;
                    question_type: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation';
                    required?: boolean;
                    allow_notes?: boolean;
                    /**
                     * 1-5 is the only rating scale
                     */
                    rating_max?: 5;
                    options?: Array<string>;
                    hint_text?: string | null;
                    weight?: number;
                }>;
            };
        },
    ): CancelablePromise<{
        id?: number;
        name?: string;
        template_type?: 'unweighted' | 'weighted' | 'text';
        updated_at?: string;
        updated_by?: string | null;
        questions?: Array<{
            id?: number;
            text?: string;
            description?: string | null;
            /**
             * recommendation is reserved: exactly one exists and it is always last
             */
            question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation';
            required?: boolean;
            /**
             * always false for free_text and recommendation
             */
            allow_notes?: boolean;
            /**
             * 1-5 is the only rating scale
             */
            rating_max?: 5;
            /**
             * dropdown choices; empty for every other type
             */
            options?: Array<string>;
            /**
             * free_text only — the hint the reviewer sees
             */
            hint_text?: string | null;
            /**
             * percent of the template total; 0 unless the scorecard is weighted
             */
            weight?: number;
            /**
             * zero-based display order
             */
            position?: number | null;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/workflow_editor/score_card_templates',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `the score-card-templates flag is off for this account`,
                422: `weights on a weighted scorecard do not total 100%`,
            },
        });
    }

    /**
     * Read one scorecard with its entries
     * @param id
     * @returns any the scorecard
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorScoreCardTemplates1(
        id: number,
    ): CancelablePromise<{
        id?: number;
        name?: string;
        template_type?: 'unweighted' | 'weighted' | 'text';
        updated_at?: string;
        updated_by?: string | null;
        questions?: Array<{
            id?: number;
            text?: string;
            description?: string | null;
            /**
             * recommendation is reserved: exactly one exists and it is always last
             */
            question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation';
            required?: boolean;
            /**
             * always false for free_text and recommendation
             */
            allow_notes?: boolean;
            /**
             * 1-5 is the only rating scale
             */
            rating_max?: 5;
            /**
             * dropdown choices; empty for every other type
             */
            options?: Array<string>;
            /**
             * free_text only — the hint the reviewer sees
             */
            hint_text?: string | null;
            /**
             * percent of the template total; 0 unless the scorecard is weighted
             */
            weight?: number;
            /**
             * zero-based display order
             */
            position?: number | null;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/score_card_templates/{id}',
            path: {
                'id': id,
            },
            errors: {
                404: `not found`,
            },
        });
    }

    /**
     * Replace a scorecard and its entries
     * @param id
     * @param requestBody
     * @returns any saved
     * @throws ApiError
     */
    public static patchInternalApiWorkflowEditorScoreCardTemplates(
        id: number,
        requestBody: {
            score_card_template: {
                name: string;
                template_type?: 'unweighted' | 'weighted' | 'text';
                /**
                 * The full question list, in display order. Any existing question left out is
                 * deleted, along with the answers already recorded against it — so omit the
                 * `questions` key entirely to leave the existing list untouched, and send an
                 * empty array only to clear it. The overall recommendation is added and moved
                 * last server-side, so the payload never has to carry it; when it is left out
                 * its `required` setting is preserved, defaulting to true on a new scorecard.
                 *
                 */
                questions?: Array<{
                    /**
                     * omit to create; ids not owned by this template are ignored
                     */
                    id?: number | null;
                    text: string;
                    description?: string | null;
                    question_type: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation';
                    required?: boolean;
                    allow_notes?: boolean;
                    /**
                     * 1-5 is the only rating scale
                     */
                    rating_max?: 5;
                    options?: Array<string>;
                    hint_text?: string | null;
                    weight?: number;
                }>;
            };
        },
    ): CancelablePromise<{
        id?: number;
        name?: string;
        template_type?: 'unweighted' | 'weighted' | 'text';
        updated_at?: string;
        updated_by?: string | null;
        questions?: Array<{
            id?: number;
            text?: string;
            description?: string | null;
            /**
             * recommendation is reserved: exactly one exists and it is always last
             */
            question_type?: 'rating' | 'yes_no' | 'dropdown' | 'free_text' | 'recommendation';
            required?: boolean;
            /**
             * always false for free_text and recommendation
             */
            allow_notes?: boolean;
            /**
             * 1-5 is the only rating scale
             */
            rating_max?: 5;
            /**
             * dropdown choices; empty for every other type
             */
            options?: Array<string>;
            /**
             * free_text only — the hint the reviewer sees
             */
            hint_text?: string | null;
            /**
             * percent of the template total; 0 unless the scorecard is weighted
             */
            weight?: number;
            /**
             * zero-based display order
             */
            position?: number | null;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/workflow_editor/score_card_templates/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `not found`,
            },
        });
    }

    /**
     * Delete a scorecard; blocked while any stage holds it
     * @param id
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiWorkflowEditorScoreCardTemplates(
        id: number,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/workflow_editor/score_card_templates/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `the score-card-templates flag is off for this account`,
                404: `not found`,
                422: `blocked — a stage still holds the scorecard`,
            },
        });
    }

    /**
     * Duplicate a scorecard into a new one owned by the current account
     * Deep-copies the scorecard (its questions) into a new template named
     * "<name> (copy)". The copy is independent — later edits on either side do
     * not propagate. Returned as a list row so it can be dropped straight into
     * the table.
     *
     * @param id
     * @returns any the new copy
     * @throws ApiError
     */
    public static postInternalApiWorkflowEditorScoreCardTemplatesClone(
        id: number,
    ): CancelablePromise<{
        id?: number;
        name?: string;
        template_type?: 'unweighted' | 'weighted' | 'text';
        questions_count?: number;
        /**
         * stages holding this scorecard, for the "Attached to" panel: attached stages and rule stages that read its result
         */
        attached_stages?: Array<{
            id?: number;
            title?: string | null;
            stage_slug?: string | null;
            /**
             * opening slug for the workflow-editor link; null when a workflow serves no opening
             */
            funnel_slug?: string | null;
            /**
             * the stage container's title: the workflow, or the opening for a funnel-owned stage
             */
            workflow?: string | null;
        }>;
        /**
         * distinct stages holding this scorecard, attached and rule stages together; blocks deletion when > 0
         */
        stage_usage_count?: number;
        updated_at?: string;
        /**
         * display name of whoever last created or edited the template; null on untouched rows
         */
        updated_by?: string | null;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/workflow_editor/score_card_templates/{id}/clone',
            path: {
                'id': id,
            },
            errors: {
                403: `the score-card-templates flag is off for this account`,
                404: `not found`,
            },
        });
    }

}